import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ROUTES } from '@/config/routes.ts';
import { resetPasswordApi } from '@/lib/auth/authApi';
import { passwordRequirements, strengthColor } from '@/lib/form/passwordStrenght.ts';
import { AlertCircle, ArrowLeft, Check, Eye, EyeOff, Lock } from 'lucide-react';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const fromSource = searchParams.get('fromSource') ?? '';

  const loginUrl = fromSource === 'site' ? `${ROUTES.LOGIN}?fromSource=site` : ROUTES.LOGIN;

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const strength = useMemo(() => passwordRequirements.filter(req => req.test(password)).length, [password]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (strength < passwordRequirements.length) {
      setError('Le mot de passe ne respecte pas les exigences de sécurité');
      return;
    }

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPasswordApi(token, password);
      setIsSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue, veuillez réessayer');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-900 to-emerald-500 p-6 font-['Plus_Jakarta_Sans',sans-serif]">
      <div className='w-full max-w-md'>
        {/* Logo */}
        <div className='text-center mb-8'>
          <div className='inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl shadow-lg mb-4'>
            <div className='w-8 h-8 rounded-full border-[6px] border-emerald-600 border-t-transparent animate-spin-slow' />
          </div>
          <h1 className='text-3xl font-extrabold text-white mb-2'>BoulesConnect</h1>
          <p className='text-emerald-100'>Réinitialisation de votre mot de passe</p>
        </div>

        {/* Card */}
        <div className='bg-white rounded-3xl shadow-2xl p-8'>
          {/* Lien invalide ou manquant */}
          {!token ? (
            <div className='text-center py-4'>
              <div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                <AlertCircle className='w-8 h-8 text-red-600' />
              </div>
              <h2 className='text-2xl font-bold text-slate-900 mb-2'>Lien invalide</h2>
              <p className='text-slate-500 text-sm mb-6'>
                Ce lien de réinitialisation est invalide ou a expiré. Veuillez faire une nouvelle demande.
              </p>
            </div>
          ) : !isSent ? (
            <>
              <h2 className='text-2xl font-bold text-slate-900 mb-2'>Votre nouveau mot de passe</h2>

              {error && (
                <div className='mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700'>
                  {error}
                </div>
              )}

              <form className='space-y-5' onSubmit={handleSubmit}>
                <div className='space-y-4'>
                  {/* Mot de passe */}
                  <div>
                    <label htmlFor='password' className='block text-sm font-semibold text-slate-700 mb-2'>
                      Mot de passe
                    </label>
                    <div className='relative'>
                      <Lock className='absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400' />
                      <input
                        id='password'
                        type={showPassword ? 'text' : 'password'}
                        required
                        minLength={8}
                        maxLength={128}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder='Créez un mot de passe'
                        className='w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors'
                      />
                      <button
                        type='button'
                        onClick={() => setShowPassword(!showPassword)}
                        className='absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors'
                      >
                        {showPassword ? <EyeOff className='w-5 h-5' /> : <Eye className='w-5 h-5' />}
                      </button>
                    </div>

                    {password.length > 0 && (
                      <div className='mt-2'>
                        <div className='flex gap-1 mb-1.5'>
                          {[0, 1, 2].map(i => (
                            <div key={i} className={`flex-1 h-1 rounded-full ${strengthColor(strength, i + 1)}`} />
                          ))}
                        </div>
                        <ul className='space-y-0.5'>
                          {passwordRequirements.map((req, i) => (
                            <li
                              key={i}
                              className={`flex items-center gap-1.5 text-xs ${req.test(password) ? 'text-emerald-600' : 'text-slate-400'}`}
                            >
                              <Check className='w-3 h-3' />
                              {req.label}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Confirmation */}
                  <div>
                    <label htmlFor='confirmPassword' className='block text-sm font-semibold text-slate-700 mb-2'>
                      Confirmer le mot de passe
                    </label>
                    <div className='relative'>
                      <Lock className='absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400' />
                      <input
                        id='confirmPassword'
                        type={showConfirm ? 'text' : 'password'}
                        required
                        maxLength={128}
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        placeholder='Répétez le mot de passe'
                        className='w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors'
                      />
                      <button
                        type='button'
                        onClick={() => setShowConfirm(!showConfirm)}
                        className='absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors'
                      >
                        {showConfirm ? <EyeOff className='w-5 h-5' /> : <Eye className='w-5 h-5' />}
                      </button>
                    </div>
                    {confirmPassword.length > 0 && password !== confirmPassword && (
                      <p className='text-xs text-red-500 mt-1'>Les mots de passe ne correspondent pas</p>
                    )}
                  </div>
                </div>

                <button
                  type='submit'
                  disabled={isSubmitting}
                  className='w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-3 rounded-xl transition shadow-lg shadow-emerald-200'
                >
                  {isSubmitting ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
                </button>
              </form>
            </>
          ) : (
            <div className='text-center py-4'>
              <div className='w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                <Check className='w-8 h-8 text-emerald-600' />
              </div>
              <h2 className='text-2xl font-bold text-slate-900 mb-2'>Mot de passe réinitialisé !</h2>
              <p className='text-slate-500 text-sm mb-4'>
                Votre mot de passe a été mis à jour. Vous pouvez maintenant vous connecter avec votre nouveau mot de
                passe.
              </p>
            </div>
          )}

          {/* Retour login */}
          <div className='mt-6 text-center'>
            <Link
              to={loginUrl}
              className='text-sm font-semibold text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1'
            >
              <ArrowLeft className='w-4 h-4' />
              Retour à la connexion
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className='mt-8 text-center'>
          <div className='flex items-center justify-center gap-6 text-sm text-emerald-100'>
            <a
              href={`${import.meta.env.VITE_URL_FRONT || 'http://localhost:5232'}/support`}
              className='hover:text-white transition'
            >
              Aide
            </a>
            <span>•</span>
            <a
              href={`${import.meta.env.VITE_URL_FRONT || 'http://localhost:5232'}/politique-rgpd`}
              className='hover:text-white transition'
            >
              Confidentialité
            </a>
            <span>•</span>
            <a
              href={`${import.meta.env.VITE_URL_FRONT || 'http://localhost:5232'}/cgu-cgv`}
              className='hover:text-white transition'
            >
              Conditions
            </a>
          </div>
          <p className='text-emerald-200 text-sm mt-4'>&copy; 2026 BoulesConnect. Tous droits réservés.</p>
        </div>
      </div>
    </div>
  );
}
